import React, { useEffect } from 'react';
import TopBar from '../../Components/TopBar';
import Footer from '../../Components/Footer';
import BigText from '../../Components/BigText';
import ToolsDemo from '../../Components/ToolsDemo';
import BuyingSectionCTA from '../../Components/BuyingSectionCTA';
import ChapterList from '../../Components/ChapterList';
import { pageView } from '../../Utilities/GoogleAnalytics';
import ReviewSection from '../../Components/ReviewSection';
import Notice from '../../Components/Notice';

const EditFileIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735325/SVGs/editfile_kk9o4n.svg';
const FolderIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735325/SVGs/folder_a9ald8.svg';
const GearIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735325/SVGs/gears_xlqlli.svg';
const BookIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735323/SVGs/book_aplu7j.svg';
const ChatIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735323/SVGs/chat_hvvzzg.svg';
const GroupIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735325/SVGs/group_snvgky.svg';
const ResearchIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735327/SVGs/research_cubgnz.svg';
const BeginnerIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735323/SVGs/beginner_mqpbuw.svg';
const TreeIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735328/SVGs/tree_is7boy.svg';
const BillIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735323/SVGs/bill_c9p4iz.svg';
const IdeaIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735325/SVGs/ideas_f447iu.svg';
const StairsIcon = 'https://res.cloudinary.com/stamped/image/upload/v1608735327/SVGs/stairs_uypish.svg';
const CheckBox = 'https://res.cloudinary.com/stamped/image/upload/v1608735323/SVGs/checkbox_kxtzhr.svg';

export default function Home() {
  //On load, let us punch some pageview
  useEffect(() => {
    pageView(window.location.pathname);
  }, []);

  const InclusionCard = ({ svg, heading, description, number }) => (
    <div className="max-w-xs p-5 m-2 text-center transition-all duration-300 bg-white shadow-sm lg:flex-3 hover:shadow-md">
      <img src={svg} className="m-auto" alt="Edit File" />
      <h1 className="mt-3 mb-3 text-xl font-bold">{heading}</h1>
      <p className="mt-2 text-sm"> {description}</p>
      <p className="mt-2 text-sm text-gray-500">{number}</p>
    </div>
  );

  const LargeCard = ({ svg, heading, description, subtitle, rotateH }) => (
    <div className="p-10 m-2 text-center transition-all duration-300 bg-white shadow-sm lg:max-w-lg hover:shadow-md">
      <img src={svg} className={`${rotateH ? 'flip-horizontal' : ''} m-auto `} alt="Edit File" />
      <h1 className="mt-3 mb-3 text-xl font-bold">{heading}</h1>
      <p className="mt-2 text-sm"> {description}</p>
      <p className="mt-6 text-xs text-gray-500 uppercase"> {subtitle} </p>
    </div>
  );

  const Inclusions = () => (
    <div className="w-4/5 m-auto">
      <div className="pb-4 mt-12 text-center lg:mt-32 bg-altBack">
        {' '}
        <p className="text-gray-500"> An all in one bundle including </p>
      </div>
      <div
        id={'cards-holder'}
        className="flex flex-col items-center justify-center w-full m-auto mt-5 cursor-pointer lg:flex-row lg:flex lg:flex-wrap flex-3"
      >
        <InclusionCard
          svg={EditFileIcon}
          heading="Ready-to-edit files"
          description="Get relevant, editable template. Approved Résumés, Approved SOPs, Approved LORs, Visa Letter and Specific Correspondence."
          number="1"
        />
        <InclusionCard
          svg={FolderIcon}
          heading="Pre-made folders"
          description="Get complete folder to file structures mapped to those templates. For your college, visa and loan applications."
          number="2"
        />
        <InclusionCard
          svg={GearIcon}
          heading="Tools for everything"
          description="Plan and visualize everything. Lifetime access to tools like Stamped’s College Finder and Timeline & Budget generator. "
          number="3"
        />
      </div>
      <div
        id={'cards-holder'}
        className="flex flex-col items-center justify-center w-full m-auto cursor-pointer lg:mb-20 lg:flex-row lg:flex lg:flex-wrap flex-3"
      >
        <InclusionCard
          svg={BookIcon}
          heading="“The Canada Guide”"
          description="E-book with easy-to-understand explanations, step-by-step-processes, printable checklists and pro-tips."
          number="4"
        />
        <InclusionCard
          svg={ChatIcon}
          heading="Chat Support"
          description="1-on-1 chat support @stamped.wiki for any question related to your Canada journey, right till the day you get there."
          number="5"
        />
        <InclusionCard
          svg={GroupIcon}
          heading="Community Support"
          description="Get access to Stamped's members-only community for feedback, updates and a friendly network of real people for life."
          number="6"
        />
      </div>
    </div>
  );

  const LargeCardsSection = () => (
    <div className="m-4 lg:m-12">
      <div
        id={'cards-holder'}
        className="flex flex-col items-center justify-center w-full m-auto mt-5 cursor-pointer lg:flex-row lg:flex lg:flex-wrap flex-2"
      >
        <LargeCard
          svg={ResearchIcon}
          heading="We’ve done in-depth research for you"
          description="At Stamped, we constantly keep updating ourselves with the latest developments in Canadian colleges, and universities. This helps us present reliable information to you. Beyond that, we are always available to chat @stamped.wiki"
        />
        <LargeCard
          svg={BeginnerIcon}
          heading="Easy to read and understand."
          description="We are focused on providing value. The purpose of writing a guide such as this is to simplify the information available everywhere. We've spent hours decluttering, so you can actually understand all alien terms (such as SDS vs non-SDS)."
        />
      </div>
      <div
        id={'cards-holder'}
        className="flex flex-col items-center justify-center w-full m-auto cursor-pointer lg:mb-20 lg:flex-row lg:flex lg:flex-wrap flex-2"
      >
        <LargeCard
          svg={TreeIcon}
          heading="Well-structured material."
          description="The guide inside your bundle comes with a special chapter index. This isn't an arbitrarily chosen index. It's ordered by priority. First things first. The structure helps you align your mental and financial assets to the goal, allowing you to achieve it efficiently."
        />
        <LargeCard
          svg={BillIcon}
          heading="Spoon-fed applications you can edit."
          description="Get complete folder-to-file structures with relevant templates. This eliminates all confusion and saves your time significantly. Our folders are labeled and structured to give you exactly everything you need for all your applications."
        />
      </div>
    </div>
  );

  const EbookBenefitSection = () => (
    <div className="w-11/12 m-auto lg:w-9/12 lg:m-auto lg:my-32">
      <div id={'cards-holder'} className="flex flex-col items-center justify-center w-full m-auto mt-5 cursor-pointer lg:flex-row lg:flex flex-2">
        <LargeCard
          svg={IdeaIcon}
          heading="Clear Explanations"
          description="Every chapter starts with an introduction. Meant to uncomplicate existing information for you."
          subtitle="To UNCOMPLICATE"
        />
        <LargeCard
          rotateH={true}
          svg={StairsIcon}
          heading="Step-by-Step Instructions"
          description="Easy-to-follow steps you can read to submit applications or book your GIC, Medical Assessment, etcetera. "
          subtitle="To assist"
        />
        <LargeCard
          svg={CheckBox}
          heading="Printable Checklists"
          description="Checklists for things, documents or tasks that you’ll need at different stages of the journey. Make your life easier."
          subtitle="To REMIND"
        />
      </div>
    </div>
  );

  const NewHome = () => (
    <div className="min-h-screen pt-32 pb-4 bg-altBack font-brand">
      <div className="m-auto text-center lg:mt-20 lg:w-10/12">
        <h1 className="text-3xl font-bold text-brandcolor font-brand lg:text-5xl">We help students get to Canada. Smartly.</h1>
        <p className="px-6 pt-4 m-auto text-gray-700 text-md lg:text-xl lg:p-0 lg:pt-4 lg:w-4/5">
          <span className="font-bold"> You don't need an agent.</span> We’ve created an all-in-one bundle for aspiring Canadian students such as you.
          Basically, it’s a digital download that includes everything you will need to get there - tools, clear information, our chat support and
          real, editable examples.{' '}
        </p>
      </div>
      {/** Inclusions Cards */}
      <Inclusions />
      <BigText />
      <LargeCardsSection />
      <ChapterList />
      <EbookBenefitSection />
      <BuyingSectionCTA />
      <ReviewSection total={6} title />
      <ToolsDemo />
      <Notice />
    </div>
  );

  return (
    <>
      <TopBar />
      <NewHome />
      <Footer />
    </>
  );
}
